import React, { useEffect, useMemo, useRef, useState } from "react";
import { Bot, Send, X, Sparkles, RotateCcw, MessageSquare } from "lucide-react";
import { apiClient } from "../lib/api";

const SUGGESTIONS = [
  "Provinsi mana paling kritis minggu ini?",
  "Ringkas 3 isu terpanas hari ini",
  "Apa rekomendasi untuk masalah pangan Sumut?",
  "Bagaimana tren sentimen media nasional?",
];

const sessionKey = () => {
  let k = sessionStorage.getItem("nms_ai_session");
  if (!k) {
    k = `nms-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    sessionStorage.setItem("nms_ai_session", k);
  }
  return k;
};

const formatReply = (text) => {
  if (!text) return null;
  // Render simple markdown: **bold**, • bullets, line breaks
  const lines = text.split("\n");
  return lines.map((line, i) => {
    const html = line
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/^•\s+/, "• ")
      .replace(/`([^`]+)`/g, "<code>$1</code>");
    return <p key={i} dangerouslySetInnerHTML={{ __html: html }} />;
  });
};

export default function AIChatPanel({ open, onClose, dashboardSnapshot }) {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Halo! Saya **Asisten AI National Monitoring System**. Tanyakan apa saja tentang kondisi nasional — risiko wilayah, harga pangan, kamtibmas, atau sentimen media. Saya akan menjawab berdasarkan data dashboard real-time.",
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionId = useMemo(() => sessionKey(), []);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setMessages((m) => [...m, { role: "user", text: message }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await apiClient.post("/ai/chat", {
        session_id: sessionId,
        message,
        context: dashboardSnapshot || null,
      });
      setMessages((m) => [...m, { role: "assistant", text: data.response || "(jawaban kosong)" }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text:
            "⚠ Maaf, terjadi kendala saat menghubungi AI. " +
            (e?.response?.data?.detail || e.message || ""),
          error: true,
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const reset = async () => {
    try {
      await apiClient.post(`/ai/reset?session_id=${sessionId}`);
    } catch {/* noop */}
    sessionStorage.removeItem("nms_ai_session");
    setMessages([
      { role: "assistant", text: "Sesi baru. Silakan ajukan pertanyaan baru tentang kondisi nasional." },
    ]);
  };

  if (!open) return null;

  return (
    <div className="ai-panel-backdrop" onClick={onClose} data-testid="ai-panel">
      <aside className="ai-panel" onClick={(e) => e.stopPropagation()}>
        <header className="ai-panel-head">
          <div className="ai-head-icon"><Bot size={16} /></div>
          <div className="ai-head-titles">
            <h3>AI Assistant</h3>
            <span><Sparkles size={10} /> GPT-5.2 · Real-time Context</span>
          </div>
          <button className="ai-head-btn" onClick={reset} title="Reset percakapan" type="button" data-testid="ai-reset">
            <RotateCcw size={14} />
          </button>
          <button className="ai-head-btn" onClick={onClose} title="Tutup" type="button" data-testid="ai-close">
            <X size={16} />
          </button>
        </header>

        <div className="ai-msg-list" ref={listRef} data-testid="ai-messages">
          {messages.map((m, i) => (
            <div key={i} className={`ai-msg ${m.role} ${m.error ? "err" : ""}`} data-testid={`ai-msg-${i}`}>
              <div className="ai-msg-avatar">
                {m.role === "user" ? "Anda" : <Bot size={14} />}
              </div>
              <div className="ai-msg-bubble">{formatReply(m.text)}</div>
            </div>
          ))}
          {busy && (
            <div className="ai-msg assistant" data-testid="ai-typing">
              <div className="ai-msg-avatar"><Bot size={14} /></div>
              <div className="ai-msg-bubble typing">
                <span /><span /><span />
              </div>
            </div>
          )}
        </div>

        {messages.length <= 1 && (
          <div className="ai-suggest" data-testid="ai-suggestions">
            <span className="ai-suggest-lbl"><MessageSquare size={11} /> CONTOH PERTANYAAN</span>
            <div className="ai-suggest-chips">
              {SUGGESTIONS.map((s, i) => (
                <button key={i} onClick={() => send(s)} type="button" data-testid={`ai-suggest-${i}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        <footer className="ai-panel-foot">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            placeholder="Ketik pertanyaan Anda…"
            rows={1}
            disabled={busy}
            data-testid="ai-input"
          />
          <button onClick={() => send()} disabled={busy || !input.trim()} type="button" data-testid="ai-send">
            <Send size={14} /> KIRIM
          </button>
        </footer>
      </aside>
    </div>
  );
}
